<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Binary_Expressions';
    let nextLink= '/Documentation/Defining_an_Editor/Buttons';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="projections-to-be-used-globally-1"
  bind:intersecting={visibleById["projections-to-be-used-globally-1"]}
  onIntersectChange={(visible) => setVisible("projections-to-be-used-globally-1", visible)}
>Projections to be used Globally</SectionComponent>
<p>For some concrete syntax elements you may provide a global definition, i.e. these definitions
are to be used everywhere in the editor.
This can only be done once, in the <em>default</em> editor definition.
However, the global definitions can be overwritten per property. </p>
<p>All global projection definitions must be enclosed in curly brackets (<code>&#123;&#125;</code>) and prefixed with the keyword <code>global</code>.</p>
<SectionComponent tag="h2" 
  id="boolean-projections-2"
  bind:intersecting={visibleById["boolean-projections-2"]}
  onIntersectChange={(visible) => setVisible("boolean-projections-2", visible)}
>Boolean Projections</SectionComponent>
<p>The keywords to be used for
the boolean <em>true</em> and <em>false</em> values can be defined globally, or you can choose to display boolean values as a boolean control.
Every option described in <a
  href="/Documentation/Defining_an_Editor/Boolean_Projections"
>Boolean Projections</a> is allowed.</p>
<p>In the example below, the user will view
every boolean property as an inner-switch button with the string <code>YES</code> for the value <code>true</code>,
and <code>NO</code> for the value <code>false</code>.</p>
<pre class="language-ts"><code class="language-ts">// Insurance/src/defs/editor-main-default.edit#L5-L18

global &#123;
    boolean inner-switch [YES | NO] // the strings used to display a boolean value, all booleans will default be displayed as an inner switch control
//    limited radio
//    limited[] checkbox
    // number slider  // you can use the slider control as default projection for numbers, but this will not often be the preferred option
    referenceSeparator [:] // the string that separates the names in a path name, e.g. pack1:cls3:part
    external &#123;
        AnimatedGif,
        ExternalCard,
        ExternalAccordion,
        ExternalDialog,
        DatePicker
    &#125;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="projections-for-limited-concepts-3"
  bind:intersecting={visibleById["projections-for-limited-concepts-3"]}
  onIntersectChange={(visible) => setVisible("projections-for-limited-concepts-3", visible)}
>Projections for Limited Concepts</SectionComponent>
<p>To indicate global definitions for limited concepts you can use the keywords <code>limited</code> and <code>limited[]</code> for
single-valued properties and multi-valued properties respectively. The keyword(s) must be followed by the
options available for limited concepts, as described
in <a
  href="/Documentation/Defining_an_Editor/Projections_for_Limited_Concepts"
>Projections for Limited Concepts</a>.</p>
<SectionComponent tag="h2" 
  id="projections-for-numbers-4"
  bind:intersecting={visibleById["projections-for-numbers-4"]}
  onIntersectChange={(visible) => setVisible("projections-for-numbers-4", visible)}
>Projections for Numbers</SectionComponent>
<p>To indicate global definitions for properties with type <code>number</code> you can use the keyword <code>number</code>. The
keyword must be followed by the options available for numbers, as described
in <a
  href="/Documentation/Defining_an_Editor/Number_Projections"
>Number Projections</a>.</p>
<SectionComponent tag="h2" 
  id="reference-separator-5"
  bind:intersecting={visibleById["reference-separator-5"]}
  onIntersectChange={(visible) => setVisible("reference-separator-5", visible)}
>Reference Separator</SectionComponent>
<p>References to other objects can consist of a series of names, like <em>country.city.street.house</em>. The string used to separate
these names (in the example above ”.”) can be set.</p>
<SectionComponent tag="h2" 
  id="adding-names-of-external-components-6"
  bind:intersecting={visibleById["adding-names-of-external-components-6"]}
  onIntersectChange={(visible) => setVisible("adding-names-of-external-components-6", visible)}
>Adding Names of External Components</SectionComponent>
<p>Freon offers the possibility to include Svelte components that are defined outside of Freon. These components
are called <a
  href="/Documentation/Customizations/External_Components"
>external components</a>. All names of external components
need to be declared in the <code>global</code> section.</p>


<PrevNextSection {prevLink} {nextLink} />